import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  useDisclosure,
  HStack,
  Spinner,
  Text,
  InputGroup,
  InputRightElement,
  Tooltip,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiCopy, FiRefreshCw } from 'react-icons/fi';

const API_BASE = '/api';

function APIKeys() {
  const toast = useToast();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [keyForm, setKeyForm] = useState({ name: '', description: '', is_active: true });
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [keyToDelete, setKeyToDelete] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchKeys = () => {
    fetch(`${API_BASE}/api-keys/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setApiKeys(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const openAddKey = () => {
    setEditingKey(null);
    setKeyForm({ name: '', description: '', is_active: true });
    onModalOpen();
  };

  const openEditKey = (key) => {
    setEditingKey(key);
    setKeyForm({ name: key.name, description: key.description || '', is_active: key.is_active });
    onModalOpen();
  };

  const handleSaveKey = async () => {
    const method = editingKey ? 'PUT' : 'POST';
    const url = editingKey ? `${API_BASE}/api-keys/${editingKey.id}/` : `${API_BASE}/api-keys/`;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(keyForm),
      });
      const data = await res.json();
      if (res.ok) {
        fetchKeys();
        toast({ title: editingKey ? 'API Key updated' : 'API Key created', status: 'success', duration: 3000 });
        onModalClose();
      } else {
        toast({ title: data.error || 'Error saving API Key', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving API Key', status: 'error', duration: 3000 });
    }
  };

  const confirmDelete = (key) => {
    setKeyToDelete(key);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!keyToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/api-keys/${keyToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchKeys();
        toast({ title: 'API Key deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Error deleting API Key', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting API Key', status: 'error', duration: 3000 });
    }
    onDeleteClose();
    setKeyToDelete(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied to clipboard', status: 'success', duration: 2000 });
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="teal.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">API Keys</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="teal" onClick={openAddKey}>
          Create API Key
        </Button>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Key</Th>
              <Th>Description</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {apiKeys.map(key => (
              <Tr key={key.id}>
                <Td fontWeight="medium">{key.name}</Td>
                <Td>
                  <HStack>
                    <Text fontSize="sm" fontFamily="mono">{key.key.slice(0, 8)}...</Text>
                    <Tooltip label="Copy to clipboard">
                      <IconButton
                        icon={<FiCopy />}
                        size="xs"
                        variant="ghost"
                        onClick={() => copyToClipboard(key.key)}
                        aria-label="Copy"
                      />
                    </Tooltip>
                  </HStack>
                </Td>
                <Td>{key.description || '-'}</Td>
                <Td>
                  <Badge colorScheme={key.is_active ? 'green' : 'red'}>
                    {key.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td>{new Date(key.created_at).toLocaleDateString()}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditKey(key)}
                      aria-label="Edit"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDelete(key)}
                      aria-label="Delete"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {apiKeys.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No API keys found</Text>
        )}
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingKey ? 'Edit API Key' : 'Create API Key'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Name</FormLabel>
              <Input
                value={keyForm.name}
                onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })}
                placeholder="My API Key"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={keyForm.description}
                onChange={(e) => setKeyForm({ ...keyForm, description: e.target.value })}
                placeholder="Description of what this key is used for"
              />
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb="0">Active</FormLabel>
              <Switch
                isChecked={keyForm.is_active}
                onChange={(e) => setKeyForm({ ...keyForm, is_active: e.target.checked })}
                colorScheme="teal"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>Cancel</Button>
            <Button colorScheme="teal" onClick={handleSaveKey}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete API Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete the API key <strong>{keyToDelete?.name}</strong>? 
            This action cannot be undone and any services using this key will stop working.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default APIKeys;
